import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { OutboxCounts } from "../../../List/schema/outbox-counts";
import { Tabs } from "./outboxTabs";
import { OutboxStatusTab } from "./outboxStatusTabs";
import { OutboxStatusTabs } from "./OutboxStatusTabs";

interface OutboxViewTabsProps {
	statusTab: OutboxStatusTab;
	onStatusTabChange: (tab: OutboxStatusTab) => void;
	counts?: OutboxCounts;
	allInclude: OutboxStatusTab[];
	onToggleInclude: (tab: OutboxStatusTab) => void;
	currentTab: Tabs;
	onTabChange: (tab: Tabs) => void;
	/** Whether the selected lead has any conversations (gates the Chat tab). */
	hasConversations: boolean;
	/** Whether any visible lead carries a feed signal (shows the Feed tab). */
	showFeedTab: boolean;
}

const tabButtonClasses = (active: boolean, disabled = false): string =>
	classNames(
		"px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer",
		active
			? "border-brand-light text-brand-light"
			: "border-transparent text-text-meta hover:text-text hover:border-gray-300",
		disabled && "opacity-40 cursor-not-allowed"
	);

/**
 * The Outbox tab row (PREC-3850): the six status tabs with counts, then the
 * content tabs (Chat / Campaign / Feed) for the selected lead.
 */
export const OutboxViewTabs: FunctionComponent<OutboxViewTabsProps> = ({
	statusTab,
	onStatusTabChange,
	counts,
	allInclude,
	onToggleInclude,
	currentTab,
	onTabChange,
	hasConversations,
	showFeedTab,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`messages.${key}`);

	const tabs = [
		{
			label: ts("tabs.chat"),
			value: Tabs.CHAT,
		},
		{
			label: ts("tabs.campaign"),
			value: Tabs.CAMPAIGN,
		},
		...(showFeedTab ? [{ label: "Feed", value: Tabs.FEED }] : []),
	];

	return (
		<div className="flex bg-white border-b border-border-light w-full flex-shrink-0">
			<OutboxStatusTabs
				activeTab={statusTab}
				allInclude={allInclude}
				counts={counts}
				onTabChange={onStatusTabChange}
				onToggleInclude={onToggleInclude}
			/>
			<div className="w-px self-stretch my-2 bg-border-light" />
			{tabs.map((tab) => {
				const isDisabled = tab.value === Tabs.CHAT && !hasConversations;

				return (
					<button
						key={tab.value}
						className={tabButtonClasses(currentTab === tab.value, isDisabled)}
						disabled={isDisabled}
						type="button"
						onClick={() => onTabChange(tab.value)}
					>
						{tab.label}
					</button>
				);
			})}
		</div>
	);
};
